import type {
  ModerationDocumentKind,
  ModerationItem,
  OrganizationKind,
  SupportTicketStatus,
} from '@/contracts';

/** The two review queues: club registrations and talent credential documents. */
export type ModerationSubject = ModerationItem['subject'];

/**
 * Every query key the console caches under, in one place.
 *
 * Keys used to be literals at each call site — the same `['admin', 'plans']` typed in
 * two screens and invalidated from a third, the moderation queue key defined three
 * times. A typo there is not a type error, it is a second cache entry that never
 * refreshes. Here a key is a function call, and the shapes that matter — which key
 * prefixes which, so one invalidation covers a list and its details — are pinned by
 * `query-keys.spec.ts`.
 *
 * Everything sits under `'admin'`, the realm, so signing out can clear the console's
 * cache with one prefix.
 */
export const queryKeys = {
  /** The shell's sidebar badges — what each queue holds right now. Every decision changes them. */
  pendingCounts: () => ['admin', 'pending-counts'] as const,
  notifications: () => ['admin', 'notifications'] as const,

  analytics: {
    summary: () => ['admin', 'analytics', 'summary'] as const,
    growth: (months: number) => ['admin', 'analytics', 'growth', months] as const,
    demandSupply: () => ['admin', 'analytics', 'demand-supply'] as const,
  },
  report: (from: string | undefined, to: string | undefined) =>
    ['admin', 'report', from, to] as const,
  clubActivity: () => ['admin', 'club-activity'] as const,
  mostSearched: () => ['admin', 'most-searched'] as const,

  payments: () => ['admin', 'payments'] as const,
  /** Read by the subscriptions screen and edited on settings: one key, one cache. */
  plans: () => ['admin', 'plans'] as const,
  policies: () => ['admin', 'policies'] as const,
  /** A mutation key, not a cache: every plan price write, so the editor can ask if one is in flight. */
  planUpdates: () => ['admin', 'plans', 'update'] as const,
  admins: () => ['admin', 'admins'] as const,

  /** Reference data (ADR-004): the specialty catalogue the rows' ids resolve against. */
  specialties: () => ['admin', 'specialties'] as const,

  talents: {
    /** The list, and the prefix of every talent's detail — a delete invalidates both. */
    all: () => ['admin', 'talents'] as const,
    detail: (candidateId: string) => ['admin', 'talents', candidateId] as const,
  },

  organizations: {
    all: () => ['admin', 'organizations'] as const,
    list: (kind: 'all' | OrganizationKind) => ['admin', 'organizations', kind] as const,
    detail: (organizationId: string) =>
      ['admin', 'organizations', 'detail', organizationId] as const,
  },

  moderation: {
    /** The list cache each subject's queue lives under — the lists and the detail share it. */
    queue: (subject: ModerationSubject) => ['admin', 'moderation', subject] as const,
    /**
     * The document queue narrowed to one kind (`?documentKind=`). Under the queue's key, so
     * a decision taken in the filtered view refreshes it with the rest.
     */
    documentKind: (kind: ModerationDocumentKind) =>
      ['admin', 'moderation', 'document', 'kind', kind] as const,
    /** The by-id read of one queued item, under the queue's key so one invalidation covers both. */
    item: (subject: ModerationSubject, itemId: string) =>
      ['admin', 'moderation', subject, 'item', itemId] as const,
  },

  support: {
    /** Every tab of the inbox; a reply or a close moves a ticket between them. */
    tickets: () => ['admin', 'support', 'tickets'] as const,
    list: (status: SupportTicketStatus) => ['admin', 'support', 'tickets', status] as const,
    ticket: (ticketId: string) => ['admin', 'support', 'ticket', ticketId] as const,
  },
} as const;
